"use client";

import { Fragment, useId, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronRightIcon, ArrowLeftIcon } from "lucide-react";
import { LinkAnchor } from "../ui/link-anchor";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/app/components/ui/sheet";
import { Button } from "../ui/button";
import { ContentSwitch } from "../animations";
import { ScrollableContainer } from "../ui/scrollable-container";
import { WiestLogo } from "../logos";
import { MENU_ITEMS, type MenuItemT } from "@/app/constants/menu-items";
import { getSafeSubmenuId } from "./submenu-id-utils";
import Link from "next/link";

/**
 * Props für die MobileMenu-Komponente
 */
type MobileMenuProps = {
  menuItems?: MenuItemT[];
  activeSubmenu: MenuItemT | null;
  onMenuItemClick: (item: MenuItemT, e: React.MouseEvent) => void;
  onBackClick: () => void;
  onClose?: () => void;
  wiestLogoHref?: string;
  tenantDisplayName?: string;
};

/**
 * Mobile Menü-Komponente für die Navigation in der Sidebar
 */
export default function MobileMenu(props: MobileMenuProps) {
  const {
    activeSubmenu,
    onMenuItemClick,
    onBackClick,
    onClose,
    menuItems = MENU_ITEMS,
    wiestLogoHref = "/",
    tenantDisplayName = "Wiest Group",
  } = props;

  // Eindeutige ID für die Content-Switch-Animation basierend auf dem aktiven Menü
  const [activeNestedSubmenu, setActiveNestedSubmenu] =
    useState<MenuItemT | null>(null);
  const contentId = activeNestedSubmenu
    ? `submenu-${getSafeSubmenuId(activeNestedSubmenu.href)}`
    : activeSubmenu
      ? `submenu-${getSafeSubmenuId(activeSubmenu.href)}`
      : "main-menu";
  const mobileMenuId = useId();
  const activeSubmenuId = activeSubmenu
    ? `${mobileMenuId}-${getSafeSubmenuId(activeSubmenu.href)}`
    : undefined;
  const activeNestedSubmenuId = activeNestedSubmenu
    ? `${mobileMenuId}-${getSafeSubmenuId(activeNestedSubmenu.href)}`
    : undefined;

  // Stellt sicher, dass MENU_ITEMS korrekt typisiert ist
  const typedMenuItems: MenuItemT[] = menuItems;

  useEffect(() => {
    setActiveNestedSubmenu(null);
  }, [activeSubmenu?.href]);

  const handleBackWithinSubmenu = () => {
    if (activeNestedSubmenu) {
      setActiveNestedSubmenu(null);
      return;
    }

    onBackClick();
  };

  // Event-Handler für Keyboard-Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Tab-Navigation für Submenu
      const displayedSubmenu = activeNestedSubmenu ?? activeSubmenu;

      if (e.key === "Tab" && displayedSubmenu) {
        const submenuId = `submenu-${getSafeSubmenuId(displayedSubmenu.href)}`;
        const submenuEl = document.getElementById(submenuId);
        if (!submenuEl) return;

        const focusableElements = submenuEl.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>;

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Wenn wir das letzte Element erreichen und Tab drücken (vorwärts)
        if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          // Submenu schließen und zurück zum Hauptmenü
          handleBackWithinSubmenu();
        }

        // Wenn wir das erste Element erreichen und Shift+Tab drücken (rückwärts)
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          // Submenu schließen und zurück zum Hauptmenü
          handleBackWithinSubmenu();
        }
        return;
      }

      // Pfeiltasten-Navigation
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        const activeElement = document.activeElement as HTMLElement;
        if (!activeElement) return;

        // Hauptmenü-Navigation (wenn kein Submenu aktiv ist)
        if (!activeSubmenu) {
          const mainMenuLinks = document.querySelectorAll(
            'nav[aria-label="Hauptnavigation"] a[role="menuitem"]'
          ) as NodeListOf<HTMLAnchorElement>;

          if (mainMenuLinks.length > 0) {
            const currentIndex = Array.from(mainMenuLinks).findIndex(
              (link) => link === activeElement
            );

            if (
              currentIndex !== -1 &&
              (e.key === "ArrowUp" || e.key === "ArrowDown")
            ) {
              e.preventDefault();
              let nextIndex;

              if (e.key === "ArrowDown") {
                nextIndex = (currentIndex + 1) % mainMenuLinks.length;
              } else {
                nextIndex =
                  currentIndex === 0
                    ? mainMenuLinks.length - 1
                    : currentIndex - 1;
              }

              mainMenuLinks[nextIndex].focus();
              return;
            }

            // Pfeil rechts: In Submenu wechseln, wenn verfügbar
            if (currentIndex !== -1 && e.key === "ArrowRight") {
              e.preventDefault();
              const currentMenuItem = mainMenuLinks[currentIndex];
              // Prüfen, ob das Element ein Submenu hat (Chevron-Icon)
              const hasSubmenu = currentMenuItem.querySelector(
                '[aria-hidden="true"]'
              );
              if (hasSubmenu) {
                // Klick simulieren, um Submenu zu öffnen
                currentMenuItem.click();
              }
              return;
            }
          }
        }

        // Submenu-Navigation
        if (displayedSubmenu) {
          const submenuId = `submenu-${getSafeSubmenuId(displayedSubmenu.href)}`;
          const submenuEl = document.getElementById(submenuId);
          if (!submenuEl) return;

          const submenuLinks = submenuEl.querySelectorAll(
            'a[role="menuitem"]'
          ) as NodeListOf<HTMLAnchorElement>;

          if (submenuLinks.length > 0) {
            const currentIndex = Array.from(submenuLinks).findIndex(
              (link) => link === activeElement
            );

            // Navigation innerhalb des Submenus
            if (
              currentIndex !== -1 &&
              (e.key === "ArrowUp" || e.key === "ArrowDown")
            ) {
              e.preventDefault();
              let nextIndex;

              if (e.key === "ArrowDown") {
                nextIndex = (currentIndex + 1) % submenuLinks.length;
              } else {
                nextIndex =
                  currentIndex === 0
                    ? submenuLinks.length - 1
                    : currentIndex - 1;
              }

              submenuLinks[nextIndex].focus();
              return;
            }

            // Zurück zum Hauptmenü mit Pfeil links
            if (e.key === "ArrowLeft" && currentIndex !== -1) {
              e.preventDefault();
              // Submenu schließen und zurück zum Hauptmenü
              handleBackWithinSubmenu();
              return;
            }
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeNestedSubmenu, activeSubmenu, onBackClick]);

  // Rendert das aktive Untermenü
  const renderSubmenu = () => {
    if (!activeSubmenu) return null;

    return (
      <div className="flex h-full flex-col">
        <SheetTitle className="sr-only">Hauptmenü</SheetTitle>
        <SheetDescription className="sr-only">
          Navigation der Wiest Group mit Untermenüs
        </SheetDescription>

        <div className="flex items-center justify-between">
          <Button
            variant="link"
            linkSize="lg"
            onClick={handleBackWithinSubmenu}
            className="-mt-2 mb-8 -ml-4 flex items-center justify-between text-sm font-medium text-black no-underline"
            aria-label="Zurück zum Hauptmenü"
          >
            <ArrowLeftIcon className="mr-2 size-4" aria-hidden="true" />
            Zurück
          </Button>
        </div>

        <h2
          id={`${activeSubmenuId}-heading`}
          className="text-md mb-2 font-semibold"
        >
          {activeSubmenu.title}
        </h2>

        <div
          className="flex-1"
          role="region"
          aria-labelledby={`${activeSubmenuId}-heading`}
          id={`submenu-${getSafeSubmenuId(activeSubmenu.href)}`}
        >
          <ScrollableContainer
            maxHeight="calc(100vh - 180px)"
            className="pb-24"
          >
            <nav aria-label={`${activeSubmenu.title} Untermenü`}>
              <ul className="space-y-1.5" role="menu">
                {activeSubmenu.children?.map((subItem, idx) => {
                  const hasChildren =
                    Array.isArray(subItem.children) &&
                    subItem.children.length > 0;

                  return (
                    <li
                      key={`${subItem.href}-${subItem.title}-${idx}`}
                      role="none"
                    >
                      <LinkAnchor
                        size="lg"
                        className="inline-flex w-fit items-center justify-between gap-2 py-1.5 text-[16px] font-normal text-black"
                        href={hasChildren ? "#" : subItem.href}
                        role="menuitem"
                        aria-haspopup={hasChildren || undefined}
                        aria-expanded={hasChildren ? false : undefined}
                        onClick={(e) => {
                          if (hasChildren) {
                            e.preventDefault();
                            setActiveNestedSubmenu(subItem);
                            return;
                          }

                          onClose?.();
                        }}
                        {...(!hasChildren && subItem.external
                          ? {
                              target: "_blank",
                              rel: "noopener noreferrer",
                              // Render as external link
                              asNextLink: false,
                            }
                          : {})}
                      >
                        {subItem.title}
                        {hasChildren ? (
                          <ChevronRightIcon
                            className="size-4"
                            strokeWidth={1.5}
                            aria-hidden="true"
                          />
                        ) : null}
                      </LinkAnchor>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </ScrollableContainer>
        </div>
      </div>
    );
  };

  const renderNestedSubmenu = () => {
    if (!activeNestedSubmenu || !activeNestedSubmenu.children) {
      return null;
    }

    return (
      <div className="flex h-full flex-col">
        <SheetTitle className="sr-only">Hauptmenü</SheetTitle>
        <SheetDescription className="sr-only">
          Navigation der Wiest Group mit Untermenüs
        </SheetDescription>

        <div className="flex items-center justify-between">
          <Button
            variant="link"
            linkSize="lg"
            onClick={handleBackWithinSubmenu}
            className="-mt-2 mb-8 -ml-4 flex items-center justify-between text-sm font-medium text-black no-underline"
            aria-label="Zurück zum Untermenü"
          >
            <ArrowLeftIcon className="mr-2 size-4" aria-hidden="true" />
            Zurück
          </Button>
        </div>

        <h2
          id={`${activeNestedSubmenuId}-heading`}
          className="text-md mb-2 font-semibold"
        >
          {activeNestedSubmenu.title}
        </h2>

        <div
          className="flex-1"
          role="region"
          aria-labelledby={`${activeNestedSubmenuId}-heading`}
          id={`submenu-${getSafeSubmenuId(activeNestedSubmenu.href)}`}
        >
          <ScrollableContainer
            maxHeight="calc(100vh - 180px)"
            className="pb-24"
          >
            <nav aria-label={`${activeNestedSubmenu.title} Untermenü`}>
              <ul className="space-y-1.5" role="menu">
                {activeNestedSubmenu.children.map((nestedItem, idx) => (
                  <li
                    key={`${activeNestedSubmenu.href}-${nestedItem.href}-${idx}`}
                    role="none"
                  >
                    <LinkAnchor
                      size="lg"
                      className="inline-flex w-fit py-1.5 text-[16px] font-normal text-black"
                      href={nestedItem.href}
                      role="menuitem"
                      onClick={() => onClose?.()}
                      {...(nestedItem.external
                        ? {
                            target: "_blank",
                            rel: "noopener noreferrer",
                            // Render as external link
                            asNextLink: false,
                          }
                        : {})}
                    >
                      {nestedItem.title}
                    </LinkAnchor>
                  </li>
                ))}
              </ul>
            </nav>
          </ScrollableContainer>
        </div>
      </div>
    );
  };

  // Rendert das Hauptmenü
  const renderMainMenu = () => {
    return (
      <>
        <SheetTitle className="sr-only">Hauptmenü</SheetTitle>
        <SheetDescription className="sr-only">
          Navigation der Wiest Group
        </SheetDescription>

        <SheetHeader className="sr-only">
          <SheetTitle>Hauptmenü</SheetTitle>
          <SheetDescription>Wiest Group Hauptnavigation</SheetDescription>
        </SheetHeader>

        <div className="flex-1 pt-12">
          <ScrollableContainer maxHeight="calc(100vh - 140px)">
            <nav aria-label="Hauptnavigation">
              <ul className="mt-4 space-y-1" role="menu">
                {typedMenuItems.map((item) => {
                  const isActive = activeSubmenu?.href === item.href;
                  const submenuId = isActive
                    ? `submenu-${getSafeSubmenuId(item.href)}`
                    : undefined;

                  return (
                    <Fragment key={item.href}>
                      {item.separatorBefore && (
                        <li
                          role="separator"
                          aria-hidden="true"
                          className="mt-6 mb-1"
                        >
                          <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide">
                            Weitere Standorte
                          </div>
                        </li>
                      )}
                      <li role="none">
                        <LinkAnchor
                          className="flex items-center justify-between py-2 text-lg font-semibold text-black"
                          href={
                            item.children && item.children.length > 0
                              ? "#"
                              : item.href
                          }
                          onClick={(e) => {
                            if (item.children && item.children.length > 0) {
                              onMenuItemClick(item, e);
                            } else {
                              onClose?.();
                            }
                          }}
                          role="menuitem"
                          aria-haspopup={!!item.children}
                          aria-expanded={isActive ? true : undefined}
                          aria-controls={
                            item.children && isActive ? submenuId : undefined
                          }
                        >
                          {item.title}
                          {item.children && (
                            <ChevronRightIcon
                              className="size-5"
                              strokeWidth={1.5}
                              aria-hidden="true"
                            />
                          )}
                        </LinkAnchor>
                      </li>
                    </Fragment>
                  );
                })}
              </ul>
            </nav>
          </ScrollableContainer>
        </div>
      </>
    );
  };

  const isMainMenu = !activeSubmenu && !activeNestedSubmenu;

  return (
    <>
      <AnimatePresence mode="wait">
        {isMainMenu && (
          <motion.div
            key="mobile-menu-logo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-4 left-6 z-10 flex h-10 items-center"
          >
            <Link
              href={wiestLogoHref}
              aria-label={`Zur Startseite von ${tenantDisplayName}`}
            >
              <WiestLogo className="h-9 w-auto" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
      <ContentSwitch id={contentId}>
        {activeNestedSubmenu
          ? renderNestedSubmenu()
          : activeSubmenu
            ? renderSubmenu()
            : renderMainMenu()}
      </ContentSwitch>
    </>
  );
}
